import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { initOneSignal, setOneSignalUser, requestPermission, getPlayerId } from '../services/oneSignal';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, profileData) {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', user.uid), {
      ...profileData,
      email,
      dateCreation: serverTimestamp(),
    });
    return user;
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  async function fetchUserProfile(uid) {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const profile = { id: snap.id, ...snap.data() };
      setUserProfile(profile);
      return profile;
    }
    return null;
  }

  // Initialise OneSignal pour un gérant et enregistre son playerId dans Firestore
  // afin que le Cloud Worker puisse lui envoyer des notifications push ciblées.
  async function setupPushNotifications(uid) {
    try {
      await initOneSignal();
      await setOneSignalUser(uid);
      await requestPermission();

      let playerId = await getPlayerId();
      // La souscription peut prendre un court instant à s'établir après la permission.
      if (!playerId) {
        await new Promise(r => setTimeout(r, 2000));
        playerId = await getPlayerId();
      }

      if (playerId) {
        await updateDoc(doc(db, 'users', uid), { oneSignalPlayerId: playerId });
        // Copie publique lisible par les clients (voir notifications.js) —
        // /users/{uid} n'est lisible que par le gérant lui-même/l'admin.
        await setDoc(doc(db, 'notifPlayers', uid), { playerId }, { merge: true });
      }
    } catch (err) {
      console.error('Erreur configuration notifications push:', err);
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const profile = await fetchUserProfile(user.uid);
        // Compte désactivé par l'admin (voir handleDelete dans
        // AdminProprietaires.js) — Firebase Auth ne peut pas être révoqué
        // depuis le client, donc on bloque ici dès la reconnexion. On ne
        // teste volontairement pas "!profile" : juste après une inscription,
        // onAuthStateChanged peut se déclencher avant que le setDoc du
        // profil (voir signup ci-dessus) ne soit terminé, ce qui donnerait
        // un faux positif sur un tout nouveau compte.
        if (profile?.compteSupprime) {
          setUserProfile(null);
          await signOut(auth);
          toast.error('Ce compte a été supprimé. Contactez l\'administrateur.');
          setLoading(false);
          return;
        }
        // Pas de notifications tant que le compte gérant n'est pas validé —
        // inutile de demander la permission navigateur avant d'avoir accès.
        if (profile?.role === 'gerant' && (!profile.statutValidation || profile.statutValidation === 'validé')) {
          setupPushNotifications(user.uid);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Pour un sous-gérant, les données (résidences, réservations...) sont
  // scopées sur l'uid du gérant principal, pas le sien — un sous-gérant n'a
  // pas de résidences à son propre nom. Pour un gérant normal, inchangé.
  const proprietaireId = userProfile?.role === 'sous-gerant' ? userProfile.gerantPrincipalId : currentUser?.uid;

  const value = { currentUser, userProfile, proprietaireId, signup, login, logout, fetchUserProfile };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
